import { useState, useEffect, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Clock, DollarSign, Package, Scissors } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  appointment: {
    id: number;
    serviceId: number;
    professionalId: number;
    startTime: Date | string;
    endTime: Date | string;
    totalPrice?: string | null;
    clientName?: string | null;
  } | null;
  salonId: number;
  onSaved?: () => void;
}

interface ServiceItem {
  serviceId: number;
  serviceName: string;
  price: string;
  durationMinutes: number;
}

interface ProductItem {
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
}

export default function AppointmentServicesModal({ open, onClose, appointment, salonId, onSaved }: Props) {
  const [activeTab, setActiveTab] = useState<"services" | "products">("services");

  // ── Serviços ──────────────────────────────────────────────────────────────
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [addingServiceId, setAddingServiceId] = useState<string>("");

  const servicesInput = useMemo(() => ({ salonId }), [salonId]);
  const { data: allServices = [] } = trpc.services.list.useQuery(servicesInput, { enabled: open && salonId > 0 });
  const { data: serviceProfessionalsMap, isLoading: isLoadingServiceProfessionals } = (trpc as any).services.getAllProfessionalsMap.useQuery(
    undefined,
    { enabled: open && !!appointment }
  );
  const { data: existingServices = [], isLoading } = trpc.appointments.getServices.useQuery(
    { appointmentId: appointment?.id ?? 0 },
    { enabled: open && !!appointment }
  );

  useEffect(() => {
    if (!open || !appointment) return;
    if (isLoading) return;
    if (existingServices.length > 0) {
      const items: ServiceItem[] = existingServices.map((es: any) => ({
        serviceId: es.serviceId,
        serviceName: es.service?.name ?? allServices.find((s) => s.id === es.serviceId)?.name ?? "Serviço",
        price: es.price,
        durationMinutes: es.durationMinutes,
      }));
      setServices(items);
    } else {
      const primary = allServices.find((s) => s.id === appointment.serviceId);
      if (primary) {
        setServices([{
          serviceId: primary.id,
          serviceName: primary.name,
          price: primary.price,
          durationMinutes: primary.durationMinutes ?? 30,
        }]);
      } else {
        setServices([]);
      }
    }
  }, [open, appointment, existingServices, isLoading, allServices]);

  const updateServices = trpc.appointments.updateServices.useMutation({
    onSuccess: () => {
      toast.success("Serviços atualizados com sucesso");
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  function addService() {
    if (!addingServiceId) return;
    const svc = allServices.find((s) => s.id === Number(addingServiceId));
    if (!svc) return;
    if (services.some((s) => s.serviceId === svc.id)) {
      toast.error("Este serviço já está na lista");
      return;
    }
    setServices((prev) => [...prev, {
      serviceId: svc.id,
      serviceName: svc.name,
      price: svc.price,
      durationMinutes: svc.durationMinutes ?? 30,
    }]);
    setAddingServiceId("");
  }

  function removeService(idx: number) {
    if (services.length <= 1) {
      toast.error("O agendamento precisa ter pelo menos um serviço");
      return;
    }
    setServices((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSaveServices() {
    if (!appointment || services.length === 0) return;
    const totalMinutes = services.reduce((acc, s) => acc + s.durationMinutes, 0);
    const totalPrice = services.reduce((acc, s) => acc + parseFloat(s.price || "0"), 0).toFixed(2);
    const newEndTime = new Date(new Date(appointment.startTime).getTime() + totalMinutes * 60000);
    const primaryServiceId = services[0]!.serviceId;
    updateServices.mutate({
      appointmentId: appointment.id,
      services: services.map((s) => ({
        serviceId: s.serviceId,
        price: s.price,
        durationMinutes: s.durationMinutes,
      })),
      primaryServiceId,
      totalPrice,
      newEndTime,
    });
  }

  // ── Produtos ──────────────────────────────────────────────────────────────
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [addingProductId, setAddingProductId] = useState<string>("");

  const productsInput = useMemo(() => ({ search: "", category: "" }), []);
  const { data: allProducts = [] } = trpc.products.list.useQuery(productsInput, { enabled: open && salonId > 0 });
  const { data: existingProducts = [], isLoading: loadingProducts } = trpc.appointments.getProducts.useQuery(
    { appointmentId: appointment?.id ?? 0 },
    { enabled: open && !!appointment }
  );

  useEffect(() => {
    if (!open || !appointment || loadingProducts) return;
    if (existingProducts.length > 0) {
      setProducts((existingProducts as any[]).map((p: any) => ({
        productId: p.productId,
        productName: p.productName,
        quantity: Number(p.quantity),
        unitPrice: Number(p.unitPrice),
      })));
    } else {
      setProducts([]);
    }
  }, [open, appointment, existingProducts, loadingProducts]);

  const filteredProducts = (allProducts as any[]).filter((p: any) =>
    Number(p.stockQty) > 0 &&
    (productSearch === "" || p.name.toLowerCase().includes(productSearch.toLowerCase()))
  );

  function addProduct() {
    const prod = (allProducts as any[]).find((p: any) => p.id === Number(addingProductId));
    if (!prod) return;
    if (products.some((p) => p.productId === prod.id)) { toast.error("Produto já adicionado"); return; }
    setProducts((prev) => [...prev, { productId: prod.id, productName: prod.name, quantity: 1, unitPrice: Number(prod.salePrice) }]);
    setAddingProductId("");
    setProductSearch("");
  }

  function removeProduct(idx: number) { setProducts((prev) => prev.filter((_, i) => i !== idx)); }
  function updateProductQty(idx: number, qty: number) {
    setProducts((prev) => prev.map((p, i) => i === idx ? { ...p, quantity: Math.max(0.001, qty) } : p));
  }

  const updateProductsMutation = trpc.appointments.updateProducts.useMutation({
    onSuccess: () => { toast.success("Produtos salvos!"); onSaved?.(); onClose(); },
    onError: (err) => toast.error(err.message),
  });

  function handleSaveProducts() {
    if (!appointment) return;
    updateProductsMutation.mutate({
      appointmentId: appointment.id,
      products: products.map((p) => ({ productId: p.productId, productName: p.productName, quantity: p.quantity, unitPrice: p.unitPrice })),
    });
  }

  // ── Totais ────────────────────────────────────────────────────────────────
  const totalMinutes = services.reduce((acc, s) => acc + s.durationMinutes, 0);
  const totalServicesPrice = services.reduce((acc, s) => acc + parseFloat(s.price || "0"), 0);
  const totalProductsPrice = products.reduce((acc, p) => acc + p.quantity * p.unitPrice, 0);
  const availableToAdd = allServices.filter((service) =>
    !services.some((selectedService) => selectedService.serviceId === service.id)
    && !!serviceProfessionalsMap?.[service.id]?.includes(appointment?.professionalId)
  );

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Editar Agendamento
            {appointment.clientName && (
              <Badge variant="outline" className="text-xs font-normal">{appointment.clientName}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Abas */}
        <div className="flex border-b border-border mb-1">
          <button
            onClick={() => setActiveTab("services")}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "services" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Scissors className="w-3.5 h-3.5" />Serviços
            {services.length > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-1">{services.length}</Badge>}
          </button>
          <button
            onClick={() => setActiveTab("products")}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "products" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Package className="w-3.5 h-3.5" />Produtos Vendidos
            {products.length > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-1">{products.length}</Badge>}
          </button>
        </div>

        {/* ── Aba Serviços ── */}
        {activeTab === "services" && (
          <div className="space-y-4">
            <div className="space-y-2">
              {isLoading ? (
                <div className="text-sm text-muted-foreground text-center py-4">Carregando serviços...</div>
              ) : services.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-4">Nenhum serviço adicionado</div>
              ) : (
                services.map((svc, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{svc.serviceName}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />{svc.durationMinutes} min
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <DollarSign className="w-3 h-3" />R$ {parseFloat(svc.price).toFixed(2)}
                        </span>
                        {idx === 0 && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1">Principal</Badge>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeService(idx)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            {isLoadingServiceProfessionals ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5 animate-pulse" />Conferindo serviços permitidos para este profissional...</p>
            ) : availableToAdd.length > 0 && (
              <div className="flex gap-2">
                <Select value={addingServiceId} onValueChange={setAddingServiceId}>
                  <SelectTrigger className="flex-1 h-9 text-sm">
                    <SelectValue placeholder="Adicionar serviço..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAdd.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name} — {s.durationMinutes ?? 30}min · R$ {parseFloat(s.price).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={addService} disabled={!addingServiceId}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}

            {services.length > 0 && (
              <div className="bg-primary/5 rounded-lg px-3 py-2 flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    Total: <strong className="text-foreground">{totalMinutes} min</strong>
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <DollarSign className="w-3.5 h-3.5" />
                    Total: <strong className="text-foreground">R$ {totalServicesPrice.toFixed(2)}</strong>
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Término: {new Date(new Date(appointment.startTime).getTime() + totalMinutes * 60000).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Aba Produtos ── */}
        {activeTab === "products" && (
          <div className="space-y-4">
            {loadingProducts ? (
              <div className="text-sm text-muted-foreground text-center py-4">Carregando...</div>
            ) : (
              <>
                {products.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">Nenhum produto adicionado ainda</div>
                ) : (
                  <div className="space-y-2">
                    {products.map((prod, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{prod.productName}</p>
                          <p className="text-xs text-muted-foreground">
                            R$ {prod.unitPrice.toFixed(2)} × {prod.quantity} = <strong className="text-foreground">R$ {(prod.unitPrice * prod.quantity).toFixed(2)}</strong>
                          </p>
                        </div>
                        <Input
                          type="number"
                          min="0.001"
                          step="1"
                          value={prod.quantity}
                          onChange={(e) => updateProductQty(idx, Number(e.target.value))}
                          className="w-16 h-7 text-xs text-center"
                        />
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeProduct(idx)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Input
                    placeholder="Buscar produto pelo nome..."
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setAddingProductId(""); }}
                    className="h-9 text-sm"
                  />
                  {productSearch.length > 0 && filteredProducts.length > 0 && (
                    <div className="border border-border rounded-lg max-h-40 overflow-y-auto bg-background shadow-sm">
                      {filteredProducts.slice(0, 8).map((p: any) => (
                        <button
                          key={p.id}
                          onClick={() => { setAddingProductId(String(p.id)); setProductSearch(p.name); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 flex items-center justify-between"
                        >
                          <span className="truncate">{p.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">
                            R$ {Number(p.salePrice).toFixed(2)} · {Number(p.stockQty)} un
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {productSearch.length > 0 && filteredProducts.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">Nenhum produto encontrado</p>
                  )}
                  <Button size="sm" variant="outline" className="w-full" onClick={addProduct} disabled={!addingProductId}>
                    <Plus className="w-4 h-4 mr-1" /> Adicionar Produto
                  </Button>
                </div>

                {products.length > 0 && (
                  <div className="bg-primary/5 rounded-lg px-3 py-2 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Package className="w-3.5 h-3.5" />{products.length} produto(s)
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <DollarSign className="w-3.5 h-3.5" />Total: <strong className="text-foreground">R$ {totalProductsPrice.toFixed(2)}</strong>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {activeTab === "services" ? (
            <Button onClick={handleSaveServices} disabled={updateServices.isPending || services.length === 0}>
              {updateServices.isPending ? "Salvando..." : "Salvar Serviços"}
            </Button>
          ) : (
            <Button onClick={handleSaveProducts} disabled={updateProductsMutation.isPending}>
              {updateProductsMutation.isPending ? "Salvando..." : "Salvar Produtos"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
